import { Form, Input, InputNumber, Space, Typography } from "antd";
import type { RuleObject, RuleRender } from "antd/es/form";
import Select from "antd/lib/select";
import { Option } from "antd/lib/mentions";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "../BusinessEditor.module.css";
import { useAppSelector } from "../../../../../app/hooks";
import { selectCurrentBusiness } from "../../../businessesSlice";
import { isNoEdit } from "../../../utils/isNoEdit";
import {
  IOnlinePurchPriceProfile,
  IPartner,
  IPurchPriceProfile,
  OnlinePartnerBucketGroups,
} from "../../../../../interfaces/partner.interface";
import { BusinessMerchantServiceChargeFee } from "../../../../../interfaces/businessFull.interface";
import { OnlinePricingBucketKey } from "../../../utils/onlinePricingSyncErrors";
import {
  BusinessOnlinePricing,
  OnlineBucketGroupCoverageEntry,
  OnlineGroupKey,
} from "../../../../../interfaces/onlinePricing.interface";
import {
  isSixteenBucketMerchant,
  onlineGroupValueType,
  CANNOT_GROUP_PRICING_NOTE,
  type OnlinePricingGroup,
} from "../../../utils/sixteenBucketOnlinePricing";
import { hasUneditableOnlinePricing } from "../../../utils/uneditableOnlinePricing";
import {
  onlineBucketValueType,
  resolveOnlineBucketRates,
  type OnlineBucketRates,
} from "../../../utils/onlineBucketRates";
import { loadPartnerWithPricing } from "./pricingPrefetch";
import {
  Band,
  formatBand,
  inPersonBandFor,
  isComparable,
  isOutOfBand,
  NOT_COMPARABLE_HINT,
  onlineBandFor,
  sixteenBucketOnlineBandFor,
  unitFor,
} from "./partnerBands";
import {
  hasInPersonGroupCoverage,
  selectPricingProfile,
} from "./pricingNewTab.helpers";
import {
  changedOnlineBucketKeys,
  OnlineBucketValues,
  withInPersonAmexFee,
} from "./merchantServiceFees.helpers";
import { selectHideInPersonAmex } from "../../../../feature-flags/featureFlagsSlice";

// Online pricing buckets (canonical source of truth). valueType is fixed per
// bucket — iDeal is a Fixed euro fee, the card buckets are percentages.
const ONLINE_BUCKETS: {
  key: OnlinePricingBucketKey;
  label: string;
  valueType: "Fixed" | "Percentage";
}[] = [
  { key: "iDeal", label: "iDeal", valueType: "Fixed" },
  { key: "amex", label: "AMEX", valueType: "Percentage" },
  {
    key: "corporateCreditCards",
    label: "Corporate Credit Cards",
    valueType: "Percentage",
  },
  {
    key: "nonCorporateCreditCards",
    label: "Non-Corporate Credit Cards",
    valueType: "Percentage",
  },
  {
    key: "appleGooglePay",
    label: "Apple Pay / Google Pay",
    valueType: "Percentage",
  },
];

// Wording shared by both sections so an out-of-band value reads the same
// whichever half of the form it sits in.
const outsideBandError = (bandText: string) => `Outside ${bandText}`;
const outsideBandWarning = (bandText: string) =>
  `Outside ${bandText} (existing value — not blocking this save)`;

// Drift already sitting in the merchant's stored in-person pricing is reported
// from the rendered value rather than waiting for a validation run, so it is
// visible the moment the pane opens — the deal wizard judges a value on render,
// and a pane that only speaks once a field is touched disagrees with it about
// when. Once the operator moves the value this yields to the field's own rule.
//
// `reportable` is the second condition the online rows need. This sets the
// Form.Item's validateStatus/help, which takes over from antd's own validation
// display, so the report has to stand down wherever the field's rule has more
// to say than "not blocking" — for the online rows, from the moment any bucket
// is edited and the whole section is on its way to the wire. The in-person rows
// are judged one at a time and have no such state, so they always report.
const useStoredDriftWarning = (
  path: (string | number)[],
  storedValue: number | undefined,
  band: Band | undefined,
  bandText: string | undefined,
  reportable = true
): { validateStatus?: "warning"; help?: string } => {
  const currentValue = Form.useWatch(path);
  if (!reportable || currentValue !== storedValue || !band || !bandText)
    return {};
  return isOutOfBand(storedValue, band)
    ? { validateStatus: "warning", help: outsideBandWarning(bandText) }
    : {};
};

// In-person flattened fee row (name + value + valueType). The value carries the
// same partner-band rule the online buckets have: the band comes from the
// merchant's purchase-price profile bucket (`value` = floor, `max` = cap)
// rather than from the online pricing.
function FeeRow({
  name,
  restField,
  band,
  storedFee,
}: {
  name: number;
  restField: any;
  band?: Band;
  storedFee?: BusinessMerchantServiceChargeFee;
}) {
  const hasBand =
    !!band && (typeof band.min === "number" || typeof band.max === "number");
  const path = ["merchantServiceChargeFees", name] as const;

  // The unit is editable on this row, so comparability is decided at validate
  // time against whatever unit the field currently holds. A Percentage fee has
  // no position inside a Fixed euro band, so it is left unvalidated and says so
  // rather than being judged against a number in another unit.
  const noOpRule: RuleObject = { validator: () => Promise.resolve() };
  const bandRule: RuleRender = (form): RuleObject => {
    const valueType = form.getFieldValue([...path, "valueType"]);
    if (!isComparable(valueType, band?.valueType)) return noOpRule;
    const edited = form.getFieldValue([...path, "value"]) !== storedFee?.value;
    const bandText = formatBand({ ...band, valueType });
    return {
      warningOnly: !edited,
      validator: (_: RuleObject, value: unknown) =>
        isOutOfBand(value, band as Band)
          ? Promise.reject(
              new Error(
                edited
                  ? outsideBandError(bandText)
                  : outsideBandWarning(bandText)
              )
            )
          : Promise.resolve(),
    };
  };

  const comparable = isComparable(storedFee?.valueType, band?.valueType);
  const bandText = comparable
    ? formatBand({ ...band, valueType: storedFee?.valueType })
    : undefined;
  const hint = !hasBand ? undefined : (bandText ?? NOT_COMPARABLE_HINT);
  const drift = useStoredDriftWarning(
    ["merchantServiceChargeFees", name, "value"],
    storedFee?.value,
    comparable ? band : undefined,
    bandText
  );

  return (
    <Space key={name} align="baseline">
      <Form.Item {...restField} name={[name, "name"]}>
        <Input className={styles.bucket_name_input} disabled />
      </Form.Item>
      <Form.Item
        {...restField}
        name={[name, "value"]}
        extra={hint}
        rules={hasBand ? [bandRule] : undefined}
        {...drift}
      >
        <InputNumber
          controls={false}
          className={styles.bucket_value_input}
          min={0}
        />
      </Form.Item>
      <Form.Item {...restField} name={[name, "valueType"]}>
        <Select className={styles.bucket_value_input} style={{ width: "85px" }}>
          <Option value={"Percentage"}>%</Option>
          <Option value={"Fixed"}>FIXED</Option>
        </Select>
      </Form.Item>
    </Space>
  );
}

function OnlineBucketRow({
  bucketKey,
  label,
  valueType,
  disabled,
  band,
  storedOnline,
  bucketKeys,
}: {
  bucketKey: OnlineGroupKey;
  label: string;
  valueType: "Fixed" | "Percentage";
  disabled: boolean;
  band?: Band;
  storedOnline?: OnlineBucketValues;
  // The key set this save may carry — the legacy buckets for a four-bucket
  // merchant, the priced groups for a 16-bucket one. Every row weighs itself
  // against the same set the save sends (see bandRule).
  bucketKeys: readonly OnlineGroupKey[];
}) {
  const form = Form.useFormInstance();
  const hasBand =
    !!band && (typeof band.min === "number" || typeof band.max === "number");
  // Flag out-of-band only while editable; a read-only (submitted) row keeps the
  // hint but drops the rule so a stored value can't block the rest of the form.
  const validateBand = hasBand && !disabled;
  const hint = hasBand ? formatBand({ ...band, valueType }) : undefined;

  // Whether this save is going to send onlinePricing at all. It is $set
  // wholesale, so the answer belongs to the section rather than to this row.
  const editedOnline = Form.useWatch("onlinePricing", form) as
    OnlineBucketValues | undefined;
  const sendsOnlinePricing =
    changedOnlineBucketKeys(editedOnline, storedOnline, bucketKeys).length > 0;

  // PC-819: the band rule is resolved per validation run (antd calls a rule
  // function with the form instance at validate time), so it can weigh this row
  // against what the save is actually about to send.
  //
  // onlinePricing is $set wholesale, so the save either omits it entirely or
  // sends all buckets — and the backend range-checks every bucket it receives
  // (validateBusinessOnlinePricingAgainstPartner). Blocking therefore tracks the
  // whole section, not this row: with no online edit pending nothing reaches the
  // wire and an out-of-band stored value is only a warning; the moment any
  // online bucket is edited every out-of-band value is on its way to a 422, so
  // we say so here instead of spending a round trip to find out.
  const bandRule: RuleRender = (form): RuleObject => {
    const changed = changedOnlineBucketKeys(
      form.getFieldValue("onlinePricing"),
      storedOnline,
      bucketKeys
    );
    const willSendOnlinePricing = changed.length > 0;
    const rowEdited = changed.includes(bucketKey);
    const bandText = formatBand({ ...band, valueType });
    const message = rowEdited
      ? outsideBandError(bandText)
      : willSendOnlinePricing
        ? // Only this section sends its untouched rows along, so only it has a
          // reason to name the fix; the shared wording covers the other two.
          `Outside ${bandText} (existing value — fix it to save online pricing)`
        : outsideBandWarning(bandText);
    return {
      warningOnly: !willSendOnlinePricing,
      validator: (_: RuleObject, value: unknown) =>
        isOutOfBand(value, band as Band)
          ? Promise.reject(new Error(message))
          : Promise.resolve(),
    };
  };

  const drift = useStoredDriftWarning(
    ["onlinePricing", bucketKey, "value"],
    storedOnline?.[bucketKey]?.value,
    band,
    hint,
    // Once the section is on the wire the rule above has the harder verdict on
    // this row, and it must be the one the operator sees.
    !sendsOnlinePricing
  );

  // The verdict on this row turns on the other buckets, and antd re-runs a
  // field's rules only when that field itself changes. So when the section
  // crosses the line onto the wire, ask for the rule now: the row the operator
  // never touched trades the report for the blocking error there and then,
  // rather than sitting silent until the next Update tells them.
  const wasSending = useRef(sendsOnlinePricing);
  useEffect(() => {
    if (wasSending.current === sendsOnlinePricing) return;
    wasSending.current = sendsOnlinePricing;
    if (!validateBand) return;
    form
      .validateFields([["onlinePricing", bucketKey, "value"]])
      .catch(() => {});
  }, [sendsOnlinePricing, validateBand, bucketKey, form]);

  // Mirror the in-person FeeRow layout so both sections look identical: a
  // read-only label, a numeric value input that keeps its full width for the
  // number, and a separate fixed-width unit control. The unit is fixed per
  // bucket (iDeal → €, cards → %) and is shown disabled — only `value` binds.
  return (
    <Space align="baseline">
      <Input className={styles.bucket_name_input} value={label} disabled />
      <Form.Item
        name={["onlinePricing", bucketKey, "value"]}
        extra={hint}
        style={{ marginBottom: 8 }}
        rules={validateBand ? [bandRule] : undefined}
        {...drift}
      >
        <InputNumber
          controls={false}
          className={styles.bucket_value_input}
          min={0}
          disabled={disabled}
        />
      </Form.Item>
      <Select
        className={styles.bucket_value_input}
        style={{ width: "85px" }}
        value={unitFor(valueType)}
        disabled
      >
        <Option value="€">€</Option>
        <Option value="%">%</Option>
      </Select>
    </Space>
  );
}

// A bucket of the 16-bucket structure, at the rate the merchant is actually
// charged for it. Read-only: this form saves through editBusiness, which has no
// per-bucket write surface — it re-derives every row from the group rate — so
// an editable row here could only ever flatten the divergence it is showing.
// Pricing (New) saves through the endpoint that does take per-bucket rows.
function OnlineBucketReadOnlyRow({
  label,
  rate,
  field,
}: {
  label: string;
  rate?: { value: number; valueType: "Fixed" | "Percentage" };
  field: string;
}) {
  return (
    <Space align="baseline">
      <Input className={styles.bucket_name_input} value={label} disabled />
      <InputNumber
        controls={false}
        className={styles.bucket_value_input}
        value={rate?.value ?? null}
        aria-label={`${label} rate`}
        disabled
      />
      <Select
        className={styles.bucket_value_input}
        style={{ width: "85px" }}
        value={unitFor(
          rate?.valueType ?? onlineBucketValueType(field) ?? "Percentage"
        )}
        disabled
      >
        <Option value="€">€</Option>
        <Option value="%">%</Option>
      </Select>
    </Space>
  );
}

export default function MerchantServiceFeesEditor({
  onlineGroups = [],
  previewOnlinePricing,
  previewCoverage,
}: {
  // Resolved by BusinessEditor, which also builds the save payload from it, so
  // the rows shown here and the keys sent are the same set by construction.
  onlineGroups?: OnlinePricingGroup[];
  previewOnlinePricing?: BusinessOnlinePricing;
  previewCoverage?: OnlineBucketGroupCoverageEntry[];
} = {}) {
  const form = Form.useFormInstance();
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const hideInPersonAmex = useAppSelector(selectHideInPersonAmex);
  const onlineDisabled = isNoEdit(currentBusiness?.noEdit, "onlinePricing");

  // The band is validated against the partner's LIVE pricing — the same band
  // the Adyen submit guard enforces — so the editor flags exactly what submit
  // would reject (the merchant's frozen snapshot band can be stale).
  const partnerId =
    (currentBusiness?.partnerId as unknown as { _id?: string })?._id ??
    (currentBusiness?.partnerId as string | undefined);
  const [partner, setPartner] = useState<IPartner>();
  useEffect(() => {
    let cancelled = false;
    if (!partnerId) {
      setPartner(undefined);
      return;
    }
    loadPartnerWithPricing(partnerId)
      .then((loaded) => {
        if (!cancelled) setPartner(loaded);
      })
      .catch(() => {
        if (!cancelled) setPartner(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);
  // Online bands come from the online price range the merchant is mapped to,
  // falling back to the partner default — the resolution the Pricing (New) tab
  // applies to the same bucket, so the two tabs judge a value alike. Unlike the
  // in-person mapping, this one has no control on this form, so the stored id is
  // the whole story.
  const onlineProfile: IOnlinePurchPriceProfile | undefined = partnerId
    ? selectPricingProfile(
        partner?.onlinePurchPriceProfiles,
        currentBusiness?.partnerOnlinePurchPriceProfileId
      )
    : undefined;

  // Bands are a partner constraint; a direct merchant (no partner) has none, so
  // never fall back to its own snapshot. The self-fallback only bridges the
  // window where a partner exists but its live pricing hasn't loaded yet.
  const bandFor = (key: keyof OnlinePartnerBucketGroups): Band | undefined =>
    partnerId
      ? onlineBandFor(key, {
          profile: onlineProfile,
          partnerDefault: partner?.defaultOnlinePricing,
        })
      : undefined;

  // PC-982: which rows this section renders is read off the merchant's
  // onlinePricingStructure. A 16-bucket merchant is priced on groups whose keys
  // the four-bucket rows below don't even contain, which is why it used to show
  // five mislabelled rows, two of them blank.
  const isSixteenBucket = isSixteenBucketMerchant(currentBusiness);
  // A structure this build cannot express in full gets no editable rows either:
  // the five below would be the whole save payload.
  const isUneditableOnline = hasUneditableOnlinePricing(currentBusiness);
  const onlineRows: {
    key: OnlineGroupKey;
    label: string;
    valueType: "Fixed" | "Percentage";
    band?: Band;
  }[] =
    isSixteenBucket || isUneditableOnline
      ? []
      : ONLINE_BUCKETS.map(({ key, label, valueType }) => ({
          key,
          label,
          valueType,
          band: bandFor(key),
        }));
  const onlineRowKeys = onlineRows.map((row) => row.key);

  // PC-1010: the rate is stored per bucket and a bucket can be priced away from
  // its group, so a group row here would show a number the merchant is not
  // charged. Resolved the way the backend resolves it — the preview's own
  // rates, else the merchant's stored rows, else the group fanned out.
  const bucketRates: OnlineBucketRates = useMemo(() => {
    if (!isSixteenBucket) return {};
    const groupRates = Object.fromEntries(
      onlineGroups.map((group) => {
        const stored = currentBusiness?.onlinePricing?.[group.key];
        return [
          group.key,
          {
            value: stored?.value ?? null,
            // The stored unit wins, so a group priced as a flat amount is not
            // read back as a rate. The default only fills an unpriced group.
            valueType: (stored?.valueType ??
              onlineGroupValueType(group.key) ??
              "Percentage") as "Fixed" | "Percentage",
          },
        ];
      })
    );
    return resolveOnlineBucketRates({
      groups: onlineGroups,
      coverage: previewCoverage,
      storedFees: currentBusiness?.onlineMerchantServiceChargeFees,
      groupRates,
    });
  }, [
    isSixteenBucket,
    onlineGroups,
    previewCoverage,
    currentBusiness?.onlineMerchantServiceChargeFees,
    currentBusiness?.onlinePricing,
  ]);

  const groupBandText = (key: OnlineGroupKey): string | undefined => {
    const band = sixteenBucketOnlineBandFor(key, {
      previewPricing: previewOnlinePricing,
      hasPartner: !!partnerId,
    });
    if (!band || (band.min === undefined && band.max === undefined))
      return undefined;
    return formatBand(band);
  };

  // In-person bands come from the purchase-price profile the merchant is mapped
  // to. The Partner Mapper pane can change that mapping in this same form, so
  // follow the form's value and fall back to what the merchant has stored.
  const formProfileId = Form.useWatch("partnerPurchPriceProfileId", form) as
    string | undefined;
  const inPersonProfile: IPurchPriceProfile | undefined = partnerId
    ? selectPricingProfile(
        partner?.purchPriceProfiles,
        formProfileId ?? currentBusiness?.partnerPurchPriceProfileId
      )
    : undefined;

  // Index-aligned with what BusinessEditor seeded into the form, so a row's
  // index maps to the fee it was seeded from.
  const storedFees = useMemo(
    () =>
      withInPersonAmexFee(currentBusiness?.merchantServiceChargeFees, {
        hideInPersonAmex,
      }),
    [currentBusiness?.merchantServiceChargeFees, hideInPersonAmex]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* In Person Payments Fees */}
      <div>
        <Typography.Title level={5} style={{ marginBottom: 12 }}>
          In Person Payments Fees
        </Typography.Title>
        {partnerId &&
          inPersonProfile &&
          !hasInPersonGroupCoverage(inPersonProfile) && (
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              {CANNOT_GROUP_PRICING_NOTE}
            </Typography.Paragraph>
          )}
        <Form.List name="merchantServiceChargeFees">
          {(fields) => (
            <div>
              {fields.map(({ key, name, ...restField }) => (
                <FeeRow
                  key={key}
                  name={name}
                  restField={restField}
                  band={inPersonBandFor(
                    storedFees[name]?.name ?? "",
                    inPersonProfile
                  )}
                  storedFee={storedFees[name]}
                />
              ))}
            </div>
          )}
        </Form.List>
      </div>

      {/* Online Payments Fees — edits the canonical onlinePricing buckets */}
      <div>
        <Typography.Title level={5} style={{ marginBottom: 12 }}>
          Online Payments Fees
        </Typography.Title>
        {isUneditableOnline && (
          <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
            This merchant is priced on an online structure this version of KBM
            cannot edit safely, so its rates are shown read-only in{" "}
            <b>Business Details</b> and saving here leaves them untouched.
          </Typography.Paragraph>
        )}
        {isSixteenBucket && (
          <>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              This merchant is priced per bucket. Rates are set in the{" "}
              <b>Pricing (New)</b> tab — saving here leaves them exactly as they
              are.
            </Typography.Paragraph>
            {onlineGroups.map((group) => {
              const bandText = groupBandText(group.key);
              return (
                <div key={group.key} style={{ marginBottom: 12 }}>
                  <Typography.Text
                    strong
                    style={{ display: "block", marginBottom: 4 }}
                  >
                    {group.label}
                    {bandText && (
                      <Typography.Text
                        type="secondary"
                        style={{ fontWeight: 400, marginLeft: 8 }}
                      >
                        {bandText}
                      </Typography.Text>
                    )}
                  </Typography.Text>
                  {group.buckets.map((bucket) => (
                    <OnlineBucketReadOnlyRow
                      key={bucket.field}
                      field={bucket.field}
                      label={bucket.label}
                      rate={bucketRates[bucket.field]}
                    />
                  ))}
                </div>
              );
            })}
          </>
        )}
        {onlineRows.map(({ key, label, valueType, band }) => (
          <OnlineBucketRow
            key={key}
            bucketKey={key}
            label={label}
            valueType={valueType}
            disabled={onlineDisabled}
            band={band}
            storedOnline={currentBusiness?.onlinePricing}
            bucketKeys={onlineRowKeys}
          />
        ))}
      </div>
    </div>
  );
}
